import { Box, Image } from '@mantine/core'
import React from 'react'
import { OwlbertMarkdown } from '@@web/shared/owlbert_markdown'
import { OWLBERT_ATTACHMENT_IMAGE_PREFIX } from '../chat/runWebChatAutoWithKbTool'

type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'image'; attachmentId: string; alt: string }

const ATTACHMENT_IMAGE_RE = new RegExp(
  `!\\[([^\\]]*)\\]\\(${OWLBERT_ATTACHMENT_IMAGE_PREFIX.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^)]+)\\)`,
  'g'
)

export function parseChatContentWithAttachmentImages(content: string): Segment[] {
  const segments: Segment[] = []
  let lastIndex = 0
  for (const match of content.matchAll(ATTACHMENT_IMAGE_RE)) {
    const index = match.index ?? 0
    if (index > lastIndex) {
      segments.push({ kind: 'text', text: content.slice(lastIndex, index) })
    }
    segments.push({
      kind: 'image',
      alt: match[1]?.trim() || 'Generated image',
      attachmentId: match[2]?.trim() ?? '',
    })
    lastIndex = index + match[0].length
  }
  if (lastIndex < content.length) {
    segments.push({ kind: 'text', text: content.slice(lastIndex) })
  }
  return segments.length ? segments : [{ kind: 'text', text: content }]
}

type AttachmentLoader = (
  attachmentId: string
) => Promise<{ mimeType: string; content: string } | null>

function AttachmentImage({
  attachmentId,
  alt,
  loadAttachment,
}: {
  attachmentId: string
  alt: string
  loadAttachment: AttachmentLoader
}) {
  const [src, setSrc] = React.useState<string | null>(null)
  const [failed, setFailed] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const row = await loadAttachment(attachmentId)
        if (cancelled || !row?.content) {
          if (!cancelled) setFailed(true)
          return
        }
        const mime = row.mimeType || 'image/png'
        setSrc(`data:${mime};base64,${row.content}`)
      } catch {
        if (!cancelled) setFailed(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [attachmentId, loadAttachment])

  if (failed) {
    return (
      <Box component="span" style={{ fontSize: 'var(--mantine-font-size-xs)', color: 'var(--mantine-color-dimmed)', fontStyle: 'italic' }}>
        Could not load generated image.
      </Box>
    )
  }
  if (!src) {
    return (
      <Box component="span" style={{ fontSize: 'var(--mantine-font-size-xs)', color: 'var(--mantine-color-dimmed)' }}>
        Loading image…
      </Box>
    )
  }
  return (
    <Image
      src={src}
      alt={alt}
      radius="md"
      maw={640}
      fit="contain"
      style={{ border: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))' }}
    />
  )
}

export function ChatMessageContent({
  content,
  loadAttachment,
}: {
  content: string
  loadAttachment?: AttachmentLoader
}) {
  const segments = React.useMemo(() => parseChatContentWithAttachmentImages(content), [content])
  const hasAttachmentImages = segments.some((s) => s.kind === 'image')

  if (!hasAttachmentImages || !loadAttachment) {
    return <OwlbertMarkdown content={content} />
  }

  return (
    <Box>
      {segments.map((segment, i) =>
        segment.kind === 'text' ? (
          segment.text ? (
            <OwlbertMarkdown key={`t-${i}`} content={segment.text} />
          ) : null
        ) : (
          <Box key={`i-${segment.attachmentId}-${i}`} my="xs">
            <AttachmentImage
              attachmentId={segment.attachmentId}
              alt={segment.alt}
              loadAttachment={loadAttachment}
            />
          </Box>
        )
      )}
    </Box>
  )
}
